import { useEffect, useMemo, useState } from 'react'

import { Divider, Input, InputNumber, Select, Switch, Typography } from 'antd'

import CustomInput from '../../../../../../components/CustomInput/customInput'
import { getMessage } from '../../../../../../util/IntlMessages'
import { useManageField } from '../../hooks/useManageFields'
import { useGetFieldDataSourceOptionsQuery } from '../../services/dataSource'
import { useGetSelectOptionsQuery } from '../../services/selectOption'
import { DEFAULT_OPERATOR_BY_KIND, OPERATORS_BY_KIND } from './data'
import { getConditionKind, getEligibleSourceFields, isOptionKind, isRequiredWhenComplete } from './helpers'

import '../FieldProperties/styles.less'

// Operador "preenchido": obrigatório quando o campo vinculado tiver qualquer valor
const FILLED_OPERATOR = 'filled'

export function RequiredFieldOptions({ setField }) {
  const { currentField, selectedFieldId, fields, getFieldById } = useManageField()

  const [draftRule, setDraftRule] = useState(currentField.requiredWhen || null)

  useEffect(() => {
    setDraftRule(currentField.requiredWhen || null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFieldId])

  const rule = draftRule

  const eligibleSourceFields = useMemo(
    () => getEligibleSourceFields({ fields, currentFieldId: selectedFieldId }),
    [fields, selectedFieldId]
  )

  const sourceField = rule?.fieldId !== undefined && rule?.fieldId !== null ? getFieldById(rule.fieldId) : null
  const sourceKind = getConditionKind(sourceField)
  const operators = sourceKind ? OPERATORS_BY_KIND[sourceKind] : []

  const isFilledOperator = rule?.operator === FILLED_OPERATOR

  const sourceHasDataSource = !!sourceField?.dataSourceOptions?.dataSourceEnumId
  const canLoadOptions = rule?.fieldId !== undefined && rule?.fieldId !== null && isOptionKind(sourceKind)

  // Opções manuais do campo (quando não usa fonte de dados).
  const { data: selectOptions } = useGetSelectOptionsQuery(
    { fieldId: rule?.fieldId },
    { skip: !canLoadOptions || sourceHasDataSource }
  )

  // Opções resolvidas da fonte de dados (quando o campo de origem usa uma).
  const { data: dataSourceOptions } = useGetFieldDataSourceOptionsQuery(
    { fieldId: rule?.fieldId },
    { skip: !canLoadOptions || !sourceHasDataSource }
  )

  const optionList = useMemo(() => {
    if (sourceHasDataSource) return dataSourceOptions || []
    return selectOptions ? selectOptions.ids.map((id) => selectOptions.entities[id]) : []
  }, [sourceHasDataSource, dataSourceOptions, selectOptions])

  const commitRule = (nextRule) => {
    setDraftRule(nextRule)

    if (nextRule && isRequiredWhenComplete(nextRule)) {
      // Regra completa: persiste.
      setField({ property: 'requiredWhen', value: nextRule, fieldId: selectedFieldId })
      return
    }

    if (currentField.requiredWhen) {
      setField({ property: 'requiredWhen', value: null, fieldId: selectedFieldId })
    }
  }

  const handleRequiredFieldChange = (value) => {
    setField({ property: 'requiredField', value, fieldId: selectedFieldId })

    if (!value) {
      setField({ property: 'requiredFromDate', value: null, fieldId: selectedFieldId })
      commitRule(null)
    }
  }

  const handleRequiredFromDateChange = (property, value) => {
    setField({ property, value: value || null, fieldId: selectedFieldId })
  }

  const handleSourceFieldChange = (fieldId) => {
    if (fieldId === undefined || fieldId === null) {
      commitRule(null)
      return
    }

    const kind = getConditionKind(getFieldById(fieldId))

    commitRule({
      fieldId,
      operator: DEFAULT_OPERATOR_BY_KIND[kind] || null,
      value: null
    })
  }

  const handleOperatorChange = (operator) => {
    commitRule({
      ...rule,
      operator,
      value: operator === FILLED_OPERATOR ? null : rule?.value ?? null
    })
  }

  const handleValueChange = (value) => {
    commitRule({ ...rule, value: value === undefined ? null : value })
  }

  const renderValueField = () => {
    if (!sourceKind) return null

    if (isOptionKind(sourceKind)) {
      return (
        <Select
          placeholder="Selecione o valor"
          value={rule?.value ?? undefined}
          onChange={handleValueChange}
          style={{ width: '100%' }}
          allowClear
          showSearch
          optionFilterProp="children"
        >
          {optionList.map((option) => (
            <Select.Option key={option.id ?? option.value} value={option.value}>
              {option.description}
            </Select.Option>
          ))}
        </Select>
      )
    }

    if (sourceKind === 'number') {
      return (
        <InputNumber
          placeholder="Informe o valor"
          value={rule?.value ?? undefined}
          onChange={handleValueChange}
          style={{ width: '100%' }}
        />
      )
    }

    return (
      <Input
        placeholder="Informe o valor"
        value={rule?.value ?? ''}
        onChange={(event) => handleValueChange(event.target.value)}
      />
    )
  }

  return (
    <div className="fieldProperties__requiredField">
      <div className="fieldProperties__requiredFieldSwitch">
        <Typography>{getMessage('isFieldRequired')}</Typography>
        <Switch checked={currentField.requiredField} onChange={handleRequiredFieldChange} />
      </div>

      <div style={{ display: currentField.requiredField ? 'block' : 'none' }} className="fieldProperties__input">
        <Typography style={{ fontWeight: 'bold' }}>Obrigatório conforme uma data?</Typography>
        <Typography className="fieldProperties__subtitle">Antes desta data, o campo será opcional.</Typography>

        <CustomInput
          type="date"
          field="requiredFromDate"
          rowSpan={50}
          setFieldValue={handleRequiredFromDateChange}
          value={{
            requiredFromDate: currentField.requiredFromDate || null
          }}
        />

        <Divider style={{ margin: '12px 0' }} />

        <Typography style={{ fontWeight: 'bold' }}>Obrigatório conforme outro campo?</Typography>
        <Typography className="fieldProperties__subtitle">
          O campo se tornará obrigatório quando a condição de valor escolhida for atendida.
        </Typography>

        <div className="fieldProperties__conditions">
          <div className="fieldProperties__input">
            <Typography style={{ fontSize: '12px' }}>Campo a ser vínculado</Typography>
            <Select
              placeholder="Selecione o campo"
              value={rule?.fieldId ?? undefined}
              onChange={handleSourceFieldChange}
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {eligibleSourceFields.map((field) => (
                <Select.Option key={field.id} value={field.id}>
                  {field.fieldName}
                </Select.Option>
              ))}
            </Select>
          </div>

          {rule?.fieldId && (
            <>
              <div className="fieldProperties__input">
                <Typography style={{ fontSize: '12px' }}>Condição</Typography>
                <Select
                  placeholder="Selecione a condição"
                  value={rule?.operator ?? undefined}
                  onChange={handleOperatorChange}
                >
                  {operators.map((operator) => (
                    <Select.Option key={operator.value} value={operator.value}>
                      {operator.label}
                    </Select.Option>
                  ))}
                </Select>
              </div>

              {rule?.operator && !isFilledOperator && (
                <div className="fieldProperties__input">
                  <Typography style={{ fontSize: '12px' }}>Valor</Typography>
                  {renderValueField()}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
